import permissionService from 'Services/api/permission.service'
import userService from 'Services/api/user.service'
import httpRequest, { httpAnonymousRequest } from 'httpRequest'
import { AuthModel, ClientType } from 'models'
import { saveEncrypted } from 'utilities/storage'
import {
    ACTION_CODE_STORAGE_KEY,
    COMPLETE_FETCH_FULL_ACCESS_TOKEN,
    COMPLETE_FETCH_PERMISSION,
} from './actions/auth.action'
import { Dispatch } from 'redux'
import clientService from 'Services/api/client.service'
import posTerminalReportService from 'Services/api/posTerminalReport.service'
import ROUTE from 'constant/route'
import { XCheftechServerKey } from 'types/storeTypes'
import { GET_CLIENT_LOADED } from './reducers/client.reducer'
import { getCurrentPosTerminal } from './actions/posTerminal.action'
import terminalEvent, {
    TerminalEventType,
} from 'websockets/events/terminalEvent'

const attachTokenKeyToRequest = (serverKey: string) => {
    httpRequest.setHeader({ key: XCheftechServerKey, value: serverKey })
    httpAnonymousRequest.setHeader({
        key: XCheftechServerKey,
        value: serverKey,
    })
}

const detachTokenKeyOnRequest = () => {
    httpRequest.removeHeader(XCheftechServerKey)
    httpAnonymousRequest.removeHeader(XCheftechServerKey)
}

export const getActionCode = async (dispatch: Dispatch<any>) => {
    const actionCodesResponse = await permissionService.getPermissionOfCurrentUser()

    let actionCodes = actionCodesResponse.data
        .map((e) => e.moduleActionID!!)
        .filter((e) => e)
    await saveEncrypted(ACTION_CODE_STORAGE_KEY, JSON.stringify(actionCodes))

    dispatch({
        type: COMPLETE_FETCH_PERMISSION,
        payload: {
            actionCodes: actionCodes,
        },
    })
}

/**
 * determine client has server key in localStorage or not.
 * this function won't check validity of key.
 */
export const hasServerKey = () => {
    return localStorage.getItem(XCheftechServerKey) !== null
}

export const setClientTokenKey = (serverKey: string) => {
    localStorage.setItem(XCheftechServerKey, serverKey)
    attachTokenKeyToRequest(serverKey)
}

/**
 * Clear invalid server key from localStorage and HTTP Header
 */
export const clearInvalidServerKey = async () => {
    const serverKey = localStorage.getItem(XCheftechServerKey)
    if (serverKey === null) return

    attachTokenKeyToRequest(serverKey)
    try {
        await clientService.validateServerKey()
    } catch (err: any) {
        console.log(err)
        localStorage.removeItem(XCheftechServerKey)
        detachTokenKeyOnRequest()
    }
}

/**
 * Finalize user login flow
 * In this step we assume that client must be previously selected (aka. `XChefTechServerkey` must be present in localStorage)
 * Ideally, this function should be called from 2 situations
 * 1) after user complete PIN login (this assumes users has previous selected device)
 * 2) after user select or create device from `RegisterDevicePage`
 * @param realAuthModelResult Real Auth model (which included `clientID`)
 */
export const finalizeUserLoginFlow = async (
    realAuthModelResult: AuthModel,
    dispatch: Dispatch<any>,
    history: any,
) => {
    // replace real access token to request
    httpRequest.setAuthorizationCookies(
        realAuthModelResult.token!,
        realAuthModelResult.refreshToken!,
    )

    const [userResult, currentClient] = await Promise.all([
        userService.getCurrentUserData(),
        clientService.getCurrentClient(),
    ])
    await getActionCode(dispatch)
    dispatch({
        type: COMPLETE_FETCH_FULL_ACCESS_TOKEN,
        payload: {
            user: userResult.data,
        },
    })

    dispatch({ type: GET_CLIENT_LOADED, payload: currentClient.data })

    dispatch(getCurrentPosTerminal())

    // emit terminalEvent throughtout the app
    terminalEvent.next({
        eventType: TerminalEventType.OnSwitchTerminal,
        data: currentClient.data.clientID!,
    })

    // TBD : It's a good idea or not to redirect to route dispatch
    if (currentClient.data.type === ClientType.OrderQuesTerminal) {
        history.replace(ROUTE.ORDER_QUEUES)
    } else if (currentClient.data.type === ClientType.PosTerminalWithCash) {
        const openedCashReport = Boolean(
            (
                await posTerminalReportService.getCurrentTerminalOpenedCashReport()
            ).data,
        )
        if (!openedCashReport) {
            history.replace(ROUTE.CASH_DRAWER_TERMINAL)
        } else {
            history.replace(ROUTE.MAIN)
        }
    } else {
        history.replace(ROUTE.MAIN)
    }
}
